import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ActionList, StatusMessage } from '../../../ui';
import { localAcknowledge } from '../../../../services/bureauManagement/bureauManagement.service';
import { getProfile, listUsers } from '../../../../services/userManagement/userManagement.service';
import { getRecipient } from '../../../../services/distribution/distribution.service';
import { useAuthContext } from '../../../../contexts/useAuthContext';
import { formatDateTime } from '../../../../helpers/formatDateTime';
import { getAlarmSections, alarmSectionsToText } from '../../../../helpers/formatAlarmClipboard';
import type { BureauAlarm } from '../../../../services/bureauManagement/bureauManagement.types';
import { alarmAcknowledgedEvent } from '../../../../services/events/alarmEvents';
import Modal from '../../../ui/Modal';
import { ModalContent } from '../../../ui/ModalContent';

interface Props {
  filteredData: BureauAlarm[];
  selectedIds?: Record<number, boolean>;
  onToggleSelect?: (id: number) => void;
  onToggleSelectAll?: () => void;
  onSelect?: (item: BureauAlarm) => void;
  onAcknowledged?: (count?: number, alarmIds?: number[]) => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

export const BureauCategoryCards: React.FC<Props> = ({ 
  filteredData, 
  selectedIds = {},
  onToggleSelect,
  onToggleSelectAll,
  onSelect, 
  onAcknowledged,
  onSuccess,
  onError
}) => {
  const { userProfile } = useAuthContext();
  const [acknowledgingId, setAcknowledgingId] = useState<number | null>(null);
  const [userMap, setUserMap] = useState<Record<string, string>>({});
  const [acknowledgedCache, setAcknowledgedCache] = useState<Record<number, { userId: string; userName: string; timestamp: string }>>({});
  
  // Modal state
  const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
  const [modalHeadingText, setModalHeadingText] = useState<string | undefined>(undefined);
  const [modalMessageText, setModalMessageText] = useState<string | undefined>(undefined);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);
  const [openViewModal, setOpenViewModal] = useState<boolean>(false);
  const [viewAlarm, setViewAlarm] = useState<BureauAlarm | null>(null);

  // Fetch users on mount to create GUID -> name mapping
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await listUsers();
        if (response?.success && response.data) {
          const map: Record<string, string> = {};
          response.data.forEach(user => {
            if (user.id) {
              const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
              map[user.id] = fullName || user.email || user.employeeId || 'Unknown User';
            }
          });
          setUserMap(map);
        }
      } catch (error) {
        console.info('Failed to fetch users for acknowledgment mapping:', error);
      }
    };
    fetchUsers();
  }, []);

  // Helper function to get acknowledged by display name
  const getAcknowledgedByName = (alarm: BureauAlarm): string | null => {
    const alarmId = alarm.bureauAlarmId || alarm.id;
    
    if (alarmId && acknowledgedCache[alarmId]) {
      return acknowledgedCache[alarmId].userName;
    }
    
    const localAckByName = (alarm as any).localAckByName || (alarm as any).LocalAckByName;
    if (localAckByName) {
      return localAckByName;
    }
    
    const userGuid = (alarm as any).localAckBy || (alarm as any).LocalAckBy || alarm.acknowledgedBy;
    
    if (userGuid && userMap[userGuid]) {
      return userMap[userGuid];
    }
    
    if (alarm.acknowledgedByName) {
      return alarm.acknowledgedByName;
    }
    
    return null;
  };

  const waitForModalConfirm = (): Promise<boolean> => {
    return new Promise((resolve) => {
      (resolverRef as React.MutableRefObject<((value: boolean) => void) | null>).current = resolve;
      setOpenModalConfirm(true);
    });
  };

  const handleAcknowledge = async (alarm: BureauAlarm) => {
    const alarmId = alarm.bureauAlarmId || alarm.id;
    if (!alarmId) {
      onError?.('Alarm ID is missing');
      return;
    }

    // Build detailed modal content with alarm information
    const modalContent = (
      <div>
        <div className="form-entry mb-2">
          <div className="form-entry__heading">
            <h3 className='heading__title--building'>
              {alarm.siteName || alarm.siteIdentifier || `Alarm ${alarmId}`}
            </h3>
          </div>
          <div className="form-row">
            <label>Site</label>
            <div className="form-row__info">{alarm.siteName || alarm.siteIdentifier || 'Unknown'}</div>
          </div>
          <div className="form-row">
            <label>Recipient</label>
            <div className="form-row__info">{formatRecipientName(alarm.bureauRecipientName || alarm.bureauName)}</div>
          </div>
          <div className="form-row">
            <label>Path</label>
            <div className="form-row__info">{alarm.pathToAlarm || alarm.path || alarm.alarmIdentifier || '-'}</div>
          </div>
          <div className="form-row">
            <label>State</label>
            <div className="form-row__info">{alarm.bureauAlarmState || 'Unknown'}</div>
          </div>
          <div className="form-row">
            <label>Last Occurrence</label>
            <div className="form-row__info">{formatDateTime(alarm.lastOccurrenceAt)}</div>
          </div>
          <div className="form-row">
            <label>Status</label>
            <div className="form-row__info">{alarm.localAck ? 'Already Acknowledged' : 'Not Acknowledged'}</div>
          </div>
          {alarm.localAck && getAcknowledgedByName(alarm) && (
            <div className="form-row">
              <label>Acknowledged By</label>
              <div className="form-row__info">{getAcknowledgedByName(alarm)}</div>
            </div>
          )}
          {alarm.localAck && ((alarm as any).localAckAt || (alarm as any).LocalAckAt || alarm.acknowledgedAt) && (
            <div className="form-row">
              <label>Acknowledged At</label>
              <div className="form-row__info">{formatDateTime((alarm as any).localAckAt || (alarm as any).LocalAckAt || alarm.acknowledgedAt)}</div>
            </div>
          )}
        </div>
      </div>
    );

    setModalMessageText(modalContent as any);
    setModalHeadingText("Acknowledge Alarm");
    setOpenModalConfirm(true);

    const confirmed = await waitForModalConfirm();

    if (!confirmed) return;

    setAcknowledgingId(alarmId);

    try {
      const profileResponse = await getProfile();
      if (!profileResponse?.success || !profileResponse.data?.id) {
        onError?.('Unable to retrieve user profile');
        setAcknowledgingId(null);
        return;
      }

      const acknowledgedBy = profileResponse.data.id;
      const userName = userProfile?.preferred_username || userProfile?.email || userProfile?.name || 'Unknown User';

      const payload = { acknowledgedBy, userName };
      const response = await localAcknowledge(alarmId, payload);
      if (response?.success) {
        // Cache the acknowledgment info locally since backend doesn't return it
        setAcknowledgedCache(prev => ({
          ...prev,
          [alarmId]: {
            userId: acknowledgedBy,
            userName: userName,
            timestamp: new Date().toISOString()
          }
        }));
        
        // Emit event to notify Sidebar to refresh
        alarmAcknowledgedEvent.emit();
        onAcknowledged?.(1, [alarmId]);
        setTimeout(() => onSuccess?.(`Alarm ${alarmId} acknowledged successfully`), 300);
      } else {
        onError?.(response?.error?.message || 'Failed to acknowledge alarm');
      }
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Failed to acknowledge alarm');
    } finally {
      setAcknowledgingId(null);
    }
  };

  const handleCopy = async (alarm: BureauAlarm) => {
    try {
      const acknowledgedByName = getAcknowledgedByName(alarm);
      const sections = getAlarmSections(alarm, acknowledgedByName);
      const body = alarmSectionsToText(sections);

      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(body);
      } else {
        const ta = document.createElement('textarea');
        ta.value = body;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        const anyDoc: any = document;
        if (anyDoc.execCommand) anyDoc.execCommand('copy');
        document.body.removeChild(ta);
      }

      onSuccess?.('Copied alarm details to clipboard');
    } catch (err) {
      onError?.('Failed to copy alarm details');
    }
  };

  const handleEmail = async (alarm: BureauAlarm) => {
    try {
      const recipientId = (alarm as any).bureauRecipientId ?? null;
      if (!recipientId) {
        onError?.('No email recipient configured for this alarm');
        return;
      }

      const res = await getRecipient(recipientId);
      if (!res || !res.success || !res.data) {
        onError?.('Failed to lookup recipient');
        return;
      }

      const recipient = res.data;

      if (!recipient.isEmailRecipient) {
        console.info('Recipient is not configured for email:', recipient.contactType);
        onError?.(`This recipient (${recipient.displayName || recipientId}) is configured for ${recipient.contactType || 'bureau'} notifications, not email. Please add an email recipient to the distribution group.`);
        return;
      }

      const email = (recipient.contactValue || '').trim();

      if (!email || !email.includes('@')) {
        console.info('Invalid or missing email address:', email);
        onError?.('Recipient does not have a valid email address');
        return;
      }

      const acknowledgedByName = getAcknowledgedByName(alarm);
      const subject = `[Alarm] ${alarm.siteName || alarm.siteIdentifier || ''} ${alarm.alarmIdentifier ? `- ${alarm.alarmIdentifier}` : ''}`.trim();
      const sections = getAlarmSections(alarm, acknowledgedByName);
      const body = alarmSectionsToText(sections);
      const to = (email || '').trim();
      const mailto = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

      const a = document.createElement('a');
      a.href = mailto;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      onSuccess?.(`Opening email to ${to}`);
    } catch (err) {
      onError?.('Failed to open email compose');
    }
  };

  const handleView = (alarm: BureauAlarm) => {
    setViewAlarm(alarm);
    setOpenViewModal(true);
  };

  // Helper to replace "Bureau" (and variations like "Bureau Bureau") with "Connected Services Hub"
  const formatRecipientName = (value: string | undefined | null): string => {
    if (!value || value === '—' || value === 'Unknown') {
      return value || 'Unknown';
    }
    // If the value is just "Bureau" repeated (e.g., "Bureau", "Bureau Bureau"), replace with "Connected Services Hub"
    if (/^(Bureau\s*)+$/i.test(value.trim())) {
      return 'Connected Services Hub';
    }
    return value;
  };

  if (filteredData.length === 0) {
    return <div className="card-results card-results-nodata-text">No Results</div>;
  }

  return (
    <>
      <Modal
        isOpen={openModalConfirm}
        title={modalHeadingText || "Confirm Action"}
        content={<ModalContent
          heading={modalHeadingText}
          message={modalMessageText} />}
        onConfirm={() => {
          resolverRef.current?.(true);
          setOpenModalConfirm(false);
        }}
        onCancel={() => {
          resolverRef.current?.(false);
          setOpenModalConfirm(false);
        }}
      />
      <Modal
        isOpen={openViewModal}
        title={viewAlarm ? `Alarm ${viewAlarm.bureauAlarmId || viewAlarm.id || ''}` : 'Alarm Details'}
        content={(
          <div>
            {viewAlarm ? (
              <div className="modal__body">
                <div><strong>Site Name:</strong> {viewAlarm.siteName || viewAlarm.siteIdentifier || '—'}</div>
                <div><strong>Site Identifier:</strong> {viewAlarm.siteIdentifier || '—'}</div>
                <div><strong>Recipient:</strong> {formatRecipientName(viewAlarm.bureauRecipientName || viewAlarm.bureauName || viewAlarm.bureauEndpoint || '—')}</div>
                <div><strong>Path:</strong> {viewAlarm.pathToAlarm || viewAlarm.path || viewAlarm.alarmIdentifier || '—'}</div>
                <div><strong>Priority:</strong> {viewAlarm.priority !== null && viewAlarm.priority !== undefined ? viewAlarm.priority : 'Not Set'}</div>
                <div><strong>Last Occurrence:</strong> {formatDateTime(viewAlarm.lastOccurrenceAt)}</div>
                <div><strong>Acknowledged:</strong> {viewAlarm.localAck ? 'Yes' : 'No'}</div>
                {viewAlarm.localAck && getAcknowledgedByName(viewAlarm) && (
                  <div><strong>Acknowledged By:</strong> {getAcknowledgedByName(viewAlarm)}</div>
                )}
                {viewAlarm.localAck && ((viewAlarm as any).localAckAt || (viewAlarm as any).LocalAckAt || viewAlarm.acknowledgedAt) && (
                  <div><strong>Acknowledged At:</strong> {formatDateTime((viewAlarm as any).localAckAt || (viewAlarm as any).LocalAckAt || viewAlarm.acknowledgedAt)}</div>
                )}
                <div><strong>Source Alarm State:</strong> {viewAlarm.sourceAlarmState ?? '—'}</div>
                <div><strong>Alarm Message:</strong> {viewAlarm.alarmMessage ?? '—'}</div>
              </div>
            ) : (
              <div>No data</div>
            )}
          </div>
        )}
        onConfirm={() => setOpenViewModal(false)}
        onCancel={() => setOpenViewModal(false)}
      />
      
      {filteredData.map((item, index) => {
        const itemId = (item.bureauAlarmId || item.id) ?? 0;
        const acknowledgedByName = getAcknowledgedByName(item);
        const siteKey = item.siteRegistryId ?? item.siteGuid ?? item.siteIdentifier ?? null;
        const siteLink = siteKey ? `/sites/${encodeURIComponent(String(siteKey))}` : `/alarm/edit/${item.bureauAlarmId ?? item.id ?? index}`;
        
        return (
          <div key={item.id ?? index} className="card-results col-12-xs col-6-md col-4-lg col-3-xl">
            <article className="card card--grid">
              <div className="card__content">
                <div className="card__content--header">
                 <div className="card__content--header-left">
                    {onToggleSelect ? (
                      <input 
                        type="checkbox" 
                        checked={!!selectedIds[itemId]}
                        onChange={() => onToggleSelect(itemId)}
                      />
                    ) : (
                      <input 
                        type="checkbox" 
                        checked={!!selectedIds[itemId]}
                        readOnly
                      />
                    )}
                    <h3>
                      <Link to={siteLink} className="alarm-name-heading">
                        {item.siteName || item.siteIdentifier || 'Unknown'}
                      </Link>
                    </h3>
                  </div>
                  
                  <div className="actions">
                    <div className="actions__list">
                      <ActionList 
                        customActions={[
                          ...(!item.localAck ? [{
                            label: 'Acknowledge',
                            onClick: () => handleAcknowledge(item),
                            className: 'actions__item actions__item--acknowledge',
                            disabled: acknowledgingId !== null,
                          }] : []),
                          {
                            label: 'View',
                            onClick: () => handleView(item),
                            className: 'actions__item actions__item--view',
                          },
                          {
                            label: 'Email',
                            onClick: () => handleEmail(item),
                            className: 'actions__item actions__item--email',
                          },
                          {
                            label: 'Copy',
                            onClick: () => handleCopy(item),
                            className: 'actions__item actions__item--copy',
                          }
                        ]}
                        disabled={acknowledgingId !== null}
                      />
                    </div>
                  
                </div>
                </div>

                <p><b>Recipient:</b> {formatRecipientName(item.bureauRecipientName || item.bureauName || item.bureauEndpoint)}</p>
                <p><b>Alarm State:</b> {item.bureauAlarmState || 'Unknown'}</p>
                <p><b>Priority:</b> {item.priority !== null && item.priority !== undefined ? item.priority : 'Not Set'}</p>
                <p className="path-name"><b>Path:</b> {item.pathToAlarm || item.path || item.alarmIdentifier || '-'}</p>
                <p><b>Last:</b> {formatDateTime(item.lastOccurrenceAt)}</p>
                <p className={item.localAck ? "acknowledged-line card-acknowledged" : "acknowledged-line"}>
                  {item.localAck ? <span className="status__acknowledged" /> : <span className="status__unacknowledged" />}
                  <b>{item.localAck ? 'Acknowledged' : 'Unacknowledged'}</b>
                </p>
                {item.localAck && acknowledgedByName && (
                  <p><b>Acknowledged By:</b> {acknowledgedByName}</p>
                )}
                {item.localAck && ((item as any).localAckAt || (item as any).LocalAckAt || item.acknowledgedAt) && (
                  <p><b>Acknowledged At:</b> {formatDateTime((item as any).localAckAt || (item as any).LocalAckAt || item.acknowledgedAt)}</p>
                )}
              </div>
            </article>
          </div>
        );
      })}
    </>
  );
};
